import XUILoader from '@xero/xui/react/loader';
import { XUIPanel } from '@xero/xui/react/panel';
import XUIStepper from '@xero/xui/react/stepper';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { beginFreeTrialSelectors } from '../../../redux/slices/BeginFreeTrialSlice';
import { ErrorBanner } from '../../../components/ErrorBanner';
import { IErrorBannerModel } from '../../../components/ErrorBanner/ErrorBannerModel';
import { eventLabels } from '../../../static/constants/eventLabels';
import { IShopifySettingsPutRequest, IShopifySettingsResponse, ITaxAuthority } from '../../../models';
import { clearingAccountSelectors, paymentGatewayChanged } from '../../../redux/slices/AccountSetupSlice';
import { installationSelectors } from '../../../redux/slices/InstallationSlice';
import { settingsSelectors, updateSettingsBegin, updateSettingsSuccess } from '../../../redux/slices/SettingsSlice';
import { userSelectors } from '../../../redux/slices/UserSlice';
import { organisationSelectors } from '../../../redux/slices/OrganisationSlice';
import { deepClone, useAppDispatch } from '../../../redux/store';
import { beanieService } from '../../../services/beanie/shopify';
import { localStorageKeys } from '../../../static/constants';
import lang from '../../../static/lang/en-NZ';
import { NavPanelSection } from '../Common';
import { PaymentGatewaysSetupTab } from './PaymentGatewaysSetupTab';
import { AdditionalSettingsTab } from './AdditionalSettingsTab';
import { ExpensesSetupTab } from './ExpensesSetupTab';
import {
    checkShopifyFlag,
    checkAllShopifyFlags,
    ShopifyValidation,
    ShopifySetupBankAccountDeleted,
    PaymentGatewayBankAccountDeletedState,
} from './models';
import { RevenueSetupTab } from './RevenueSetupTab';
import './ShopifySetupPanel.css';
import { AccountAndTaxSetupTab } from './AccountAndTaxSetupTab';

export interface IShopifySetupPanelProps {
    isSaving: boolean;
    onCancelClick: () => void;
    onSaveClick: (
        settings: Partial<IShopifySettingsPutRequest>,
        isFinal?: boolean,
        startTrialOnFinalSave?: boolean
    ) => Promise<void>;
    flags: any;
}

/**
 * Returns the name of the page from the page number
 * @param currentPage The current page
 * @param version The version of the installation
 * @returns The name of the page
 */
function getPageName(currentPage: number, version: number | undefined) {
    if (version === 2) {
        switch (currentPage) {
            case 0:
            default:
                return eventLabels.SHOPIFY_ACCOUNT_AND_TAX_SETUP;
            case 1:
                return eventLabels.SHOPIFY_ACCOUNT_SETUP;
            case 2:
                return eventLabels.SHOPIFY_SYNC_HISTORY;
        }
    } else {
        switch (currentPage) {
            case 0:
            default:
                return eventLabels.SHOPIFY_ACCOUNT_SETUP;
            case 1:
                return eventLabels.SHOPIFY_REVENUE;
            case 2:
                return eventLabels.SHOPIFY_EXPENSES;
            case 3:
                return eventLabels.SHOPIFY_SYNC_HISTORY;
        }
    }
}

/**
 * Convert a string into something that can be used as a dom id
 * @param name a string to make id worthy
 */
function makeId(name: string): string {
    return name.toLocaleLowerCase().replace(/\s/g, '-');
}

/**
 * The setup page for a Shopify connection
 * @param param0 Setup panel properties as defined by IShopifySetupPanelProps
 * @returns A react function component
 */
const ShopifySetupPanelProto: FC<IShopifySetupPanelProps> = ({ isSaving, onCancelClick, onSaveClick, flags }) => {
    const dispatch = useAppDispatch();

    const installation = useSelector(installationSelectors.installation);
    const settings = useSelector(settingsSelectors.settings) as IShopifySettingsResponse;
    const user = useSelector(userSelectors.user);
    const organisation = useSelector(organisationSelectors.organisation);
    const isCreatingClearingAccount = useSelector(clearingAccountSelectors.isCreating);
    const isFetchingSettings = useSelector(settingsSelectors.isFetching);
    const isStartingTrial = useSelector(beginFreeTrialSelectors.isStartingTrial);

    const [updatedFields, setUpdatedFields] = useState<string[]>([]);
    const [canSubmit, setCanSubmit] = useState<ShopifyValidation>(ShopifyValidation.NONE);
    const [currentPage, setCurrentPage] = useState(0);
    const [showRefreshPlanError, setShowRefreshPlanError] = useState(false);

    const integrationLang: any = lang[installation.integrationType];
    const paymentGatewayBankAccountDeletedState: PaymentGatewayBankAccountDeletedState =
        useState<ShopifySetupBankAccountDeleted>({});

    useEffect(() => {
        window.localStorage.removeItem(localStorageKeys.SKIP_WIZARD);
    }, []);

    useEffect(() => {
        // only do the check if we have finished fetching the settings
        if (isFetchingSettings) {
            return;
        }

        const { paymentGatewayDetails, bankAccounts } = settings;

        if (!Array.isArray(bankAccounts)) {
            return;
        }

        const bankAccountIds = bankAccounts.map((bankAccount) => bankAccount.accountId);

        if (!paymentGatewayDetails) {
            return;
        }

        for (const [gateway, details] of Object.entries(paymentGatewayDetails)) {
            // if the bank or clearing account is defined in the settings but there is not an associated bank account in Xero

            const hasMissingBankAccount = details.bankAccountId
                ? !bankAccountIds.includes(details.bankAccountId ?? '')
                : false;
            const hasMissingClearingAccount = details.clearingAccountId
                ? !bankAccountIds.includes(details.clearingAccountId ?? '')
                : false;

            // if we have needed to clear a clearing account or a bank account, record this payment gateway
            if (hasMissingBankAccount || hasMissingClearingAccount) {
                const paymentGatewayDetailsToUpdate = {
                    ...paymentGatewayDetails[gateway],
                };

                if (hasMissingBankAccount) {
                    paymentGatewayDetailsToUpdate.bankAccountId = null;
                    paymentGatewayDetailsToUpdate.bankAccountCurrency = null;
                }

                if (hasMissingClearingAccount) {
                    paymentGatewayDetailsToUpdate.clearingAccountId = null;
                    paymentGatewayDetailsToUpdate.clearingAccountCurrency = null;
                    paymentGatewayDetailsToUpdate.clearingAccountName = null;
                    paymentGatewayDetailsToUpdate.clearingAccountNumber = null;
                }

                dispatch(paymentGatewayChanged(gateway, paymentGatewayDetailsToUpdate));

                paymentGatewayBankAccountDeletedState[1]({
                    ...paymentGatewayBankAccountDeletedState[0],
                    [gateway]: true,
                });
            }
        }
        // All dependencies used in a hook should be injected here.
        // This is to ensure that the application reacts to all state and state does not get out of sync.
        // When working in this hook please take the time to refactor, remove this comment and resolve the dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetchingSettings]);

    const handleSettingsChange =
        <K extends keyof IShopifySettingsPutRequest>(settingName: K) =>
        (settingValue: IShopifySettingsPutRequest[K]) => {
            dispatch(updateSettingsSuccess({ [settingName]: settingValue }));
            addToUpdatedFields(settingName);
        };

    const handleTaxRateChange = (
        taxAuthorities: ITaxAuthority[],
        selectedTaxAuthority: string,
        selectedTaxType: string,
        selectedTaxRate: number
    ) => {
        const index = taxAuthorities.findIndex((ta) => ta.xeroTaxAuthority === selectedTaxAuthority);
        if (index !== -1) {
            taxAuthorities[index] = {
                ...taxAuthorities[index],
                xeroTaxRate: selectedTaxRate,
                xeroTaxType: selectedTaxType,
            };
        } else {
            taxAuthorities = [
                ...taxAuthorities,
                {
                    xeroTaxAuthority: selectedTaxAuthority,
                    xeroTaxType: selectedTaxType,
                    xeroTaxRate: selectedTaxRate,
                },
            ];
        }

        dispatch(
            updateSettingsSuccess({
                taxAuthorities: [...taxAuthorities],
            } as Partial<IShopifySettingsPutRequest>)
        );
        addToUpdatedFields('taxRate');
    };
    const handleShopifyRegionChange = (
        taxAuthorities: ITaxAuthority[],
        selectedTaxAuthority: string,
        shopifyTaxRegion: string
    ) => {
        const index = taxAuthorities.findIndex((ta) => ta.xeroTaxAuthority === selectedTaxAuthority);
        if (index !== -1) {
            taxAuthorities[index] = {
                ...taxAuthorities[index],
                shopifyTaxRegionCode: shopifyTaxRegion,
            };
        } else {
            taxAuthorities = [
                ...taxAuthorities,
                {
                    xeroTaxAuthority: selectedTaxAuthority,
                    shopifyTaxRegionCode: shopifyTaxRegion,
                },
            ];
        }
        dispatch(
            updateSettingsSuccess({
                taxAuthorities: [...taxAuthorities],
            } as Partial<IShopifySettingsPutRequest>)
        );
        addToUpdatedFields('shopifyRegion');
    };
    const handleDateRangeChange = (from: Date | null, to: Date | null) => {
        dispatch(
            updateSettingsSuccess({
                syncFromDateUtc: from,
                syncToDateUtc: to,
            } as Partial<IShopifySettingsPutRequest>)
        );
        addToUpdatedFields('dateRange');
    };
    const handlePageValid =
        (validator: ShopifyValidation) =>
        (isValid: boolean): void => {
            if (isValid) {
                setCanSubmit(canSubmit | validator);
            } else {
                // turn off every flag (means nothing is valid anymore)
                // then turn on the flag for each page before the current
                let newCanSubmit = ShopifyValidation.NONE;
                for (const otherValidator in ShopifyValidation) {
                    if (Object.prototype.hasOwnProperty.call(ShopifyValidation, otherValidator)) {
                        const myOtherValidator: ShopifyValidation =
                            ShopifyValidation[otherValidator as keyof typeof ShopifyValidation];
                        if (myOtherValidator === validator) {
                            break;
                        }
                        newCanSubmit |= myOtherValidator;
                    }
                }
                setCanSubmit(newCanSubmit);
            }
        };
    const trackBeanieEvent = async (page: number, version: number | undefined) => {
        switch (getPageName(page, version)) {
            case eventLabels.SHOPIFY_ACCOUNT_SETUP:
                await beanieService.sendOutboundSettingsStepUpdatedEvent(
                    installation.integrationType,
                    'account setup',
                    organisation.id,
                    user,
                    updatedFields
                );
                break;
            case eventLabels.SHOPIFY_REVENUE:
                await beanieService.sendOutboundSettingsStepUpdatedEvent(
                    installation.integrationType,
                    'revenue setup',
                    organisation.id,
                    user,
                    updatedFields
                );
                break;
            case eventLabels.SHOPIFY_EXPENSES:
                await beanieService.sendOutboundSettingsStepUpdatedEvent(
                    installation.integrationType,
                    'expenses setup',
                    organisation.id,
                    user,
                    updatedFields
                );
                break;
            case eventLabels.SHOPIFY_SYNC_HISTORY:
                await beanieService.sendOutboundSettingsStepUpdatedEvent(
                    installation.integrationType,
                    'additional settings setup',
                    organisation.id,
                    user,
                    updatedFields
                );
                break;
            case eventLabels.SHOPIFY_ACCOUNT_AND_TAX_SETUP:
                await beanieService.sendOutboundSettingsStepUpdatedEvent(
                    installation.integrationType,
                    'account and tax setup',
                    organisation.id,
                    user,
                    updatedFields
                );
                break;
        }
    };

    function _trackOnboardingAccountAndTaxInteraction() {
        // Only track on version 2 as on version 1 this page does not exist
        const isAccountAndTaxSetupPage = currentPage === 0 && installation.versionSupport?.currentVersion === 2;

        if (isAccountAndTaxSetupPage && installation.isOnboarded === false) {
            const failedToSuggestTax = Boolean(
                settings.accountAndTaxMappings.find((accountAndTaxMapping) => !accountAndTaxMapping.xeroTaxTypeOnLoad)
            );
            const failedToSuggestAccount = Boolean(
                settings.accountAndTaxMappings.find((accountAndTaxMapping) => !accountAndTaxMapping.xeroAccountIdOnLoad)
            );

            const hasChangedAccount = Boolean(
                settings.accountAndTaxMappings.find(
                    (accountAndTaxMapping) =>
                        accountAndTaxMapping.xeroAccountIdOnLoad &&
                        accountAndTaxMapping.xeroAccountId !== accountAndTaxMapping.xeroAccountIdOnLoad
                )
            );

            const hasChangedTaxRate = Boolean(
                settings.accountAndTaxMappings.find(
                    (accountAndTaxMapping) =>
                        accountAndTaxMapping.xeroTaxTypeOnLoad &&
                        accountAndTaxMapping.xeroTaxType !== accountAndTaxMapping.xeroTaxTypeOnLoad
                )
            );

            const failedToSuggestAllAccountsAndTax = failedToSuggestTax || failedToSuggestAccount;
            const hasAccountOrTaxBeenChangedFromSuggested = hasChangedAccount || hasChangedTaxRate;

            if (failedToSuggestAllAccountsAndTax) {
                beanieService.sendOutboundConnectionOnboardingAccountTaxEvent(
                    installation.integrationType,
                    'Unable to auto set defaults for account and or tax',
                    organisation.id,
                    user
                );
            }

            if (hasAccountOrTaxBeenChangedFromSuggested) {
                beanieService.sendOutboundConnectionOnboardingAccountTaxEvent(
                    installation.integrationType,
                    'User changed tax or account mapping',
                    organisation.id,
                    user
                );
            }

            const userAcceptedDefaultsNoChanges =
                !failedToSuggestAllAccountsAndTax && !hasAccountOrTaxBeenChangedFromSuggested;

            if (userAcceptedDefaultsNoChanges) {
                beanieService.sendOutboundConnectionOnboardingAccountTaxEvent(
                    installation.integrationType,
                    'User accepted tax and account mapping with no changes',
                    organisation.id,
                    user
                );
            }
        }
    }

    const handleSubmit = useCallback(async () => {
        const numberOfPages = pages().length;
        const isLastPage = currentPage === numberOfPages - 1;
        const isFinalSave = checkAllShopifyFlags(canSubmit, numberOfPages) && isLastPage;
        const settingsToUpdate = deepClone(settings);

        _trackOnboardingAccountAndTaxInteraction();

        if (settingsToUpdate.paymentGatewayDetails) {
            dispatch(updateSettingsBegin());
            if (isFinalSave) {
                dispatch(
                    updateSettingsSuccess({
                        ...settingsToUpdate,
                        syncFromDateUtc: settings.syncFromDateUtc,
                        syncToDateUtc: settings.syncToDateUtc,
                    } as Partial<IShopifySettingsPutRequest>)
                );
            } else {
                const paymentGatewayDetails = deepClone(settingsToUpdate.paymentGatewayDetails);
                delete paymentGatewayDetails[integrationLang.SETUP_PAGE.STORE.ROW_SELECT_PAYMENT_TYPE];
                settingsToUpdate.paymentGatewayDetails = paymentGatewayDetails;

                dispatch(updateSettingsSuccess(settingsToUpdate as Partial<IShopifySettingsPutRequest>));
            }
        }

        await onSaveClick(settingsToUpdate as Partial<IShopifySettingsPutRequest>, isFinalSave, true);
        await trackBeanieEvent(currentPage, installation.versionSupport?.currentVersion);

        if (!isLastPage) {
            setCurrentPage(currentPage + 1);
        }
        // All dependencies used in a hook should be injected here.
        // This is to ensure that the application reacts to all state and state does not get out of sync.
        // When working in this hook please take the time to refactor, remove this comment and resolve the dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canSubmit, currentPage, settings]);

    const handleCancel = useCallback(() => {
        onCancelClick();
        setShowRefreshPlanError(false);
    }, [currentPage, installation.versionSupport?.currentVersion, onCancelClick]);

    const addToUpdatedFields = (value: string) => {
        if (!updatedFields.find((f) => f.toLowerCase() === value.toLowerCase())) {
            setUpdatedFields([...updatedFields, value]);
        }
    };

    const pages = (): Array<{ content: ReactNode; validator: ShopifyValidation }> => {
        if (installation.versionSupport?.currentVersion === 2) {
            return pagesVersion2;
        } else {
            return pagesVersion1;
        }
    };

    const tabs = (): [] => {
        if (installation.versionSupport?.currentVersion === 2) {
            return tabsVersion2;
        } else {
            return tabsVersion1;
        }
    };

    const pagesVersion1: Array<{ content: ReactNode; validator: ShopifyValidation }> = [
        {
            content: (
                <PaymentGatewaysSetupTab
                    integrationLang={integrationLang}
                    onGatewayChange={handleSettingsChange('paymentGatewayDetails')}
                    onValid={handlePageValid(ShopifyValidation.FIRST_PAGE)}
                    showTaxDisclaimer={!settings?.supportsTaxCollection!}
                    key={0}
                    paymentGatewayBankAccountDeletedState={paymentGatewayBankAccountDeletedState}
                />
            ),
            validator: ShopifyValidation.FIRST_PAGE,
        },
        {
            content: (
                <RevenueSetupTab
                    integrationLang={integrationLang}
                    onSetSalesAccount={handleSettingsChange('salesAccountId')}
                    onSetDiscountsAccount={handleSettingsChange('discountsAccountId')}
                    onSetShippingsAccount={handleSettingsChange('shippingAccountId')}
                    onSetRefundsAccount={handleSettingsChange('refundsAccountId')}
                    onValid={handlePageValid(ShopifyValidation.SECOND_PAGE)}
                    key={1}
                />
            ),
            validator: ShopifyValidation.SECOND_PAGE,
        },
        {
            content: (
                <ExpensesSetupTab
                    integrationLang={integrationLang}
                    onSetGiftCards={handleSettingsChange('giftCardsAccountId')}
                    onSetTaxes={handleSettingsChange('taxCollectedAccountId')}
                    onSetFees={handleSettingsChange('feesAccountId')}
                    onValid={handlePageValid(ShopifyValidation.THIRD_PAGE)}
                    key={2}
                />
            ),
            validator: ShopifyValidation.THIRD_PAGE,
        },
        {
            content: (
                <AdditionalSettingsTab
                    integrationLang={integrationLang}
                    onValid={handlePageValid(ShopifyValidation.FOURTH_PAGE)}
                    onTaxRateSelect={handleTaxRateChange}
                    onShopifyRegionSelect={handleShopifyRegionChange}
                    onDateRangeSelect={handleDateRangeChange}
                    setShowRefreshPlanError={setShowRefreshPlanError}
                    key={3}
                    isTaxEnabledForRegion={settings?.supportsTaxCollection!}
                />
            ),
            validator: ShopifyValidation.FOURTH_PAGE,
        },
    ];

    const pagesVersion2: Array<{ content: ReactNode; validator: ShopifyValidation }> = [
        {
            content: (
                <AccountAndTaxSetupTab
                    integrationLang={integrationLang}
                    onValid={handlePageValid(ShopifyValidation.FIRST_PAGE)}
                    isValid={checkShopifyFlag(canSubmit, ShopifyValidation.FIRST_PAGE)}
                    onSetAccountAndTaxMapping={handleSettingsChange('accountAndTaxMappings')}
                    key={0}
                />
            ),
            validator: ShopifyValidation.FIRST_PAGE,
        },
        {
            content: (
                <PaymentGatewaysSetupTab
                    integrationLang={integrationLang}
                    onGatewayChange={handleSettingsChange('paymentGatewayDetails')}
                    onValid={handlePageValid(ShopifyValidation.SECOND_PAGE)}
                    showTaxDisclaimer={!settings?.supportsTaxCollection!}
                    key={1}
                    paymentGatewayBankAccountDeletedState={paymentGatewayBankAccountDeletedState}
                />
            ),
            validator: ShopifyValidation.SECOND_PAGE,
        },
        {
            content: (
                <AdditionalSettingsTab
                    integrationLang={integrationLang}
                    onValid={handlePageValid(ShopifyValidation.THIRD_PAGE)}
                    onTaxRateSelect={handleTaxRateChange}
                    onShopifyRegionSelect={handleShopifyRegionChange}
                    onDateRangeSelect={handleDateRangeChange}
                    setShowRefreshPlanError={setShowRefreshPlanError}
                    key={2}
                    isTaxEnabledForRegion={settings?.supportsTaxCollection!}
                />
            ),
            validator: ShopifyValidation.THIRD_PAGE,
        },
    ];

    const tabsVersion1: [] = useMemo(
        () =>
            integrationLang.SETUP_PAGE.TAB_NAMES.map(
                (name: string, i: number): { name: string; isComplete: boolean; isDisabled: boolean } => {
                    const isComplete = checkShopifyFlag(canSubmit, pages()[i]?.validator);
                    const isDisabled: boolean = i !== 0 && !checkShopifyFlag(canSubmit, pages()[i - 1]?.validator);
                    return { name, isComplete, isDisabled };
                }
            ),
        // All dependencies used in a hook should be injected here.
        // This is to ensure that the application reacts to all state and state does not get out of sync.
        // When working in this hook please take the time to refactor, remove this comment and resolve the dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [canSubmit, pagesVersion1]
    );

    const tabsVersion2: [] = useMemo(
        () =>
            integrationLang.SETUP_PAGE.TAB_NAMES_V2.map(
                (name: string, i: number): { name: string; isComplete: boolean; isDisabled: boolean } => {
                    const isComplete = checkShopifyFlag(canSubmit, pages()[i]?.validator);
                    const isDisabled: boolean = i !== 0 && !checkShopifyFlag(canSubmit, pages()[i - 1]?.validator);
                    return { name, isComplete, isDisabled };
                }
            ),
        // All dependencies used in a hook should be injected here.
        // This is to ensure that the application reacts to all state and state does not get out of sync.
        // When working in this hook please take the time to refactor, remove this comment and resolve the dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [canSubmit, pagesVersion2]
    );

    const intraPageNav = (
        <NavPanelSection
            cancelContent='Cancel'
            submitContent={
                currentPage === pages().length - 1
                    ? installation.isOnboarded
                        ? lang.BUTTONS.UPDATE
                        : lang.BUTTONS.CONNECT
                    : lang.BUTTONS.CONTINUE
            }
            isSaving={isSaving || isStartingTrial}
            isSubmitEnabled={checkShopifyFlag(canSubmit, pages()[currentPage]?.validator)}
            onSubmitClick={handleSubmit}
            onCancelClick={handleCancel}
            fullWidth={true}
        />
    );

    const error = {
        message: integrationLang.SETUP_PAGE.REFRESH_PLAN_ERROR_TEXT,
        actionButtonText: integrationLang.SETUP_PAGE.REFRESH,
        actionButtonOnClickAction: () => {
            window.localStorage.setItem(localStorageKeys.SKIP_WIZARD, 'true');
            window.location.reload();
        },
    } as IErrorBannerModel;

    return (isSaving || isFetchingSettings || isStartingTrial) && !isCreatingClearingAccount ? (
        <XUILoader ariaLabel='Loading' />
    ) : (
        <>
            {showRefreshPlanError && <ErrorBanner error={error} />}
            <XUIStepper
                currentStep={currentPage}
                id='shopify-setup-stepper'
                tabs={tabs()}
                updateCurrentStep={setCurrentPage}
            />
            <XUIPanel
                className='shopify-panel'
                qaHook={makeId(getPageName(currentPage, installation.versionSupport?.currentVersion))}
            >
                {pages()[currentPage]?.content}
                {intraPageNav}
            </XUIPanel>
        </>
    );
};

export const ShopifySetupPanel = withLDConsumer()(ShopifySetupPanelProto);
